import { useState } from "react";

const photoLocations = [
  { name: "CVS Pharmacy — Magazine Street", address: "900 Magazine St, New Orleans, LA 70130", hours: "Daily 7 AM – 10 PM" },
  { name: "Walgreens — Canal Street", address: "1301 Canal St, New Orleans, LA 70112", hours: "24 Hours" },
  { name: "CVS Pharmacy — Metairie Rd", address: "3101 Metairie Rd, Metairie, LA 70001", hours: "Daily 7 AM – 10 PM" },
  { name: "The UPS Store — Uptown", address: "4519 Magazine St, New Orleans, LA 70115", hours: "Call for hours" },
  { name: "Walgreens — Kenner", address: "3009 Williams Blvd, Kenner, LA 70062", hours: "24 Hours" },
  { name: "CVS Pharmacy — Gretna", address: "2701 Belle Chasse Hwy, Gretna, LA 70056", hours: "Daily 7 AM – 10 PM" },
];

const dmvLocations = [
  { name: "OMV Orleans Parish", address: "3831 N Causeway Blvd, Metairie, LA 70002", hours: "Mon–Fri, 8:00 AM – 4:30 PM", note: "Driver licenses, REAL ID, vehicle registration. Appointment recommended.", featured: true },
  { name: "OMV Gretna Branch", address: "1801 Belle Chasse Hwy, Gretna, LA 70056", hours: "Mon–Fri, 8:00 AM – 4:30 PM", note: "West Bank option for Gretna, Harvey, and Marrero residents.", featured: false },
];

export default function NewOrleansHelpfulResourcesSection() {
  const [photoExpanded, setPhotoExpanded] = useState(false);
  const visiblePhotos = photoExpanded ? photoLocations : photoLocations.slice(0, 4);

  return (
    <section id="local-resources" className="bg-[#F7F8FA] py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Local Resources</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Helpful Resources for New Orleans Travelers</h2>
          <p className="text-gray-500 max-w-2xl mx-auto text-base leading-relaxed">
            Getting your passport ready involves more than just the application. Here are local Greater New Orleans resources to help you gather everything you need.
          </p>
        </div>

        <div className="space-y-6">
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <div className="bg-[#1A365D] px-6 py-5 flex items-center gap-3">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-white/15 flex-shrink-0">
                <i className="ri-camera-line text-white text-lg"></i>
              </div>
              <div>
                <p className="text-white/60 text-xs font-semibold uppercase tracking-widest">Passport Photos</p>
                <p className="text-white font-bold text-base leading-tight mt-0.5">Passport Photo Locations Near New Orleans</p>
              </div>
            </div>
            <div className="px-6 py-6">
              <p className="text-gray-500 text-sm leading-relaxed mb-4">Your passport application requires two identical 2&quot;&times;2&quot; passport photos taken within the last 6 months.</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-4">
                {visiblePhotos.map((loc, i) => (
                  <div key={i} className="bg-[#F7F8FA] border border-gray-200 rounded-lg p-3.5">
                    <p className="font-bold text-[#1A365D] text-xs leading-snug mb-1">{loc.name}</p>
                    <p className="text-gray-500 text-xs leading-relaxed">{loc.address}</p>
                    <p className="text-gray-400 text-xs mt-1">{loc.hours}</p>
                  </div>
                ))}
              </div>
              {!photoExpanded && (
                <button onClick={() => setPhotoExpanded(true)} className="text-[#1A365D] hover:text-[#F6C344] font-semibold text-xs flex items-center gap-1.5 mb-4 cursor-pointer transition-colors">
                  <i className="ri-add-circle-line text-sm"></i> Show all {photoLocations.length} photo locations
                </button>
              )}
              <div className="bg-[#F7F8FA] border border-gray-200 rounded-md p-3">
                <p className="text-gray-600 text-xs leading-relaxed"><i className="ri-lightbulb-line text-[#F6C344] mr-1.5"></i><strong>Tip:</strong> Wear a plain white or off-white top, no glasses, neutral expression. Photos must meet strict U.S. State Department specifications.</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <div className="bg-[#1A365D] px-6 py-5 flex items-center gap-3">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-white/15 flex-shrink-0">
                <i className="ri-id-card-line text-white text-lg"></i>
              </div>
              <div>
                <p className="text-white/60 text-xs font-semibold uppercase tracking-widest">Driver&apos;s License &amp; State ID</p>
                <p className="text-white font-bold text-base leading-tight mt-0.5">Louisiana OMV — DMV Services</p>
              </div>
            </div>
            <div className="px-6 py-6">
              <p className="text-gray-500 text-sm leading-relaxed mb-5">A valid government-issued photo ID is required for your passport application. The Louisiana Office of Motor Vehicles (OMV) handles driver&apos;s licenses and REAL ID in the New Orleans area.</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-5">
                {dmvLocations.map((loc, i) => (
                  <div key={i} className={`rounded-lg p-4 border ${loc.featured ? "bg-amber-50 border-[#F6C344]/40" : "bg-[#F7F8FA] border-gray-200"}`}>
                    <p className="font-bold text-[#1A365D] text-xs leading-snug">{loc.name}</p>
                    <p className="text-gray-500 text-xs mt-1">{loc.address}</p>
                    <p className="text-gray-400 text-xs mt-0.5">{loc.hours}</p>
                    <p className="text-[#1A365D] text-xs font-medium mt-1.5">{loc.note}</p>
                  </div>
                ))}
              </div>
              <div className="flex flex-wrap gap-4">
                <a href="https://expresslane.dps.louisiana.gov/" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-[#1A365D] hover:text-[#F6C344] font-semibold text-xs transition-colors"><i className="ri-external-link-line text-sm"></i>Louisiana OMV ExpressLane</a>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <div className="bg-[#1A365D] px-6 py-5 flex items-center gap-3">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-white/15 flex-shrink-0">
                <i className="ri-flight-takeoff-line text-white text-lg"></i>
              </div>
              <div>
                <p className="text-white/60 text-xs font-semibold uppercase tracking-widest">Airports</p>
                <p className="text-white font-bold text-base leading-tight mt-0.5">Airports Serving New Orleans Travelers</p>
              </div>
            </div>
            <div className="px-6 py-6">
              <div className="bg-amber-50 border border-[#F6C344]/40 rounded-lg p-4 mb-4">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-[#1A365D] font-black text-xl">MSY</span>
                  <span className="text-xs font-bold text-amber-700 bg-amber-100 px-2 py-0.5 rounded-full">Primary</span>
                </div>
                <p className="font-bold text-[#1A365D] text-xs mb-1">Louis Armstrong New Orleans International Airport</p>
                <p className="text-gray-500 text-xs mb-1">900 Airline Dr, Kenner, LA 70062 — About 15 miles west of downtown</p>
                <p className="text-gray-400 text-xs mb-3">Direct international flights to London, Frankfurt, and Toronto. Major domestic connections to Atlanta, Houston, Dallas, and Charlotte.</p>
                <a href="https://www.flymsy.com/" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-[#1A365D] hover:text-[#F6C344] font-semibold text-xs transition-colors"><i className="ri-external-link-line text-sm"></i>MSY Airport Website</a>
              </div>
              <div className="bg-[#F7F8FA] border border-gray-200 rounded-md p-3">
                <p className="text-gray-600 text-xs leading-relaxed"><i className="ri-passport-line text-amber-500 mr-1.5"></i><strong>Passport required for all international flights.</strong> If your departure is within two weeks, standard processing won&apos;t make it in time — <a href="https://fastpassportcenter.com/checkout/" className="font-bold text-[#1A365D] underline underline-offset-2 hover:text-[#F6C344] transition-colors">expedited processing through Fast Passport Center</a> is your most reliable option.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}